import React, { useMemo } from 'react';
import Button from './Button';
import StatsPlot from './StatsPlot';

/**
 * Component to display live agent details with stats during simulation
 */
const LiveAgentDetailsPanel = ({ uid, user, telemetryHistory, currentPosition, onClose }) => {
  // Extract stats from telemetry history
  const statsHistory = useMemo(() => {
    if (!telemetryHistory || telemetryHistory.length === 0) {
      return [];
    }

    // Extract all entries that have stats
    const allStats = [];
    const seenTimestamps = new Set();

    telemetryHistory.forEach(entry => {
      if (entry.stats && entry.stats.lastUpdate && !seenTimestamps.has(entry.timestamp)) {
        seenTimestamps.add(entry.timestamp);
        allStats.push({
          timestamp: entry.timestamp,
          maxVelocitymps: entry.stats.maxVelocitymps || 0,
          totVerticalMeters: entry.stats.totVerticalMeters || 0
        });
      }
    });

    // Sort by timestamp
    allStats.sort((a, b) => a.timestamp - b.timestamp);

    return allStats;
  }, [telemetryHistory]);

  const currentTimestamp = currentPosition?.timestamp || Date.now();

  return (
    <div className="agent-details-panel">
      <div className="agent-details-header">
        <h2>{user?.name || uid}</h2>
        <Button
          variant="outline"
          size="small"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
      <div className="agent-details-content">
        {/* Stats Section - only show if we have stats */}
        {statsHistory.length > 1 && (
          <div className="telemetry-section stats-section">
            <h3>Statistics</h3>
            <StatsPlot
              data={statsHistory}
              currentTime={currentTimestamp}
              dataKey="maxVelocitymps"
              label="Max Velocity"
              unit="km/h"
              color="#667eea"
            />
            <StatsPlot
              data={statsHistory}
              currentTime={currentTimestamp}
              dataKey="totVerticalMeters"
              label="Total Vertical"
              unit="m"
              color="#764ba2"
            />
          </div>
        )}

        <div className="telemetry-section">
          <h3>Position</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Location</span>
              <span className="telemetry-value">
                {currentPosition
                  ? `${currentPosition.lat.toFixed(6)}°, ${currentPosition.lon.toFixed(6)}°`
                  : 'No location'}
              </span>
            </div>
            {currentPosition?.altitude !== null && currentPosition?.altitude !== void 0 && (
              <div className="telemetry-item">
                <span className="telemetry-label">Altitude</span>
                <span className="telemetry-value">
                  {currentPosition.altitude.toFixed(1)} m
                </span>
              </div>
            )}
            {currentPosition?.speed !== null && currentPosition?.speed !== void 0 && (
              <div className="telemetry-item">
                <span className="telemetry-label">Speed</span>
                <span className="telemetry-value">
                  {(currentPosition.speed * 3.6).toFixed(1)} km/h
                </span>
              </div>
            )}
            {currentPosition?.heading !== null && currentPosition?.heading !== void 0 && (
              <div className="telemetry-item">
                <span className="telemetry-label">Heading</span>
                <span className="telemetry-value">
                  {currentPosition.heading.toFixed(0)}°
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="telemetry-section">
          <h3>Live Status</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Type</span>
              <span className="telemetry-value">Live Agent</span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Data Points</span>
              <span className="telemetry-value">
                {telemetryHistory?.length || 0}
              </span>
            </div>
            {currentPosition?.timestamp && (
              <div className="telemetry-item">
                <span className="telemetry-label">Last Update</span>
                <span className="telemetry-value">
                  {new Date(currentPosition.timestamp).toLocaleTimeString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAgentDetailsPanel;
